<template>
  <div class="h-100">
    <template v-if="isNotarityBannerDisplayed">
      <SignedDocumentsNotarityBanner />
    </template>
    <v-container fluid
                 class="h-100 pa-0"
    >
      <template v-if="currentRoomPending">
        <div class="d-flex align-center justify-center h-100">
          <AppLoader />
        </div>
      </template>
      <template v-else-if="currentRoomFailedToLoad">
        <v-row class="flex-column h-100 text-center" align-content="center" justify="center">
          <p>{{$t('project.views.Project.errorIntro')}}</p>
          <i18n-t keypath="project.views.Project.errorFollowUp.base" tag="p" scope="global">
            <template #link>
              <a href="mailto:support@closd.com">
                <span class="text--small text--bold">
                  {{$t('project.views.Project.errorFollowUp.linkText')}}
                </span>
              </a>
            </template>
          </i18n-t>
          <AppButton color="primary" @click="reloadCurrentRoom">
            {{ $t('project.views.Project.errorBtnText') }}
          </AppButton>
        </v-row>
      </template>
      <template v-else-if="(currentRoom && !currentRoom.subscriptionDisabledFor) || isRouteSimpleSignature">
        <router-view></router-view>
      </template>
      <template v-else-if="currentRoom && currentRoom.subscriptionDisabledFor">
        <v-row class="h-100" align-content="center" justify="center">
          <SubscriptionDisableReasonsCard :reason="currentRoom.subscriptionDisabledFor" />
        </v-row>
      </template>
    </v-container>

    <!-- DIALOGS -->
    <WatermarkedViewerDialog
      v-if="watermarkedDialogData.isOpen"
      :mnemo="roomMnemo"
      @close="closeWatermarkedDialog"
    />
    <IManageAuthDialog v-if="iManageAuthDialogIsOpened" @close="iManageAuthDialogIsOpened = false"/>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import dayjs from 'dayjs'
import { useTheme } from 'vuetify'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import AppLoader from '@/common/AppLoader.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import IManageAuthDialog, { IMANAGE_REFUSAL_USER_SETTINGS_KEY } from '@/common/imanage/IManageAuthDialog.vue'
import { getRoomDefaultRoute, getRoomRouteType } from '@/common/utils/rooms'
import { toast } from '@/common/utils/toast'
import { getUserSetting } from '@/common/utils/userSettings'
import i18n from '@/plugins/i18n'
import { vuetifyOverrideColors } from '@/plugins/styles/colors.ts'
import SignedDocumentsNotarityBanner from '@/project/signed-documents/SignedDocumentsNotarityBanner.vue'
import {
  ENVELOPE_TEMPLATE_ADD_ROUTE_NAME,
  ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME,
  NOT_FOUND_ROUTE_NAME,
  QUESTION_ANSWER_ADD_ROUTE_NAME,
  QUESTION_ANSWER_EDIT_ROUTE_NAME,
  ROOM_DOCUMENTS_ROUTE_NAME,
  ROOM_ROUTE_NAME,
  ROOMS_ROUTE_NAME,
  ROOM_ROUTE_TYPES,
  ROOM_CHECKLIST_ROUTE_NAME,
  ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME,
  ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
  SIGNING_CHECKLIST_ADD_ROUTE_NAME,
} from '@/router/routerData'
import { getRouter } from '@/router/routerInstance'
import store from '@/store'
import { FilesMutationTypes } from '@/store/modules/files/mutation_types'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { START_AUTH_TIMER, GET_AUTH_STATUS } from '@/store/modules/imanage/action_types'
import { GET_CURRENT_ROOM, UPDATE_ACCESS_TIME } from '@/store/modules/room/action_types'
import { SET_CURRENT_ROOM_ROUTE_DECIDED } from '@/store/modules/room/mutation_types'
import SubscriptionDisableReasonsCard from '@/subscriptions/SubscriptionDisableReasonsCard.vue'

import WatermarkedViewerDialog from '../documents/watermarked-viewer/WatermarkedViewerDialog.vue'

async function loadCurrentRoom (roomMnemo) {
  store.commit(`room/${SET_CURRENT_ROOM_ROUTE_DECIDED}`, false)
  return store.dispatch(`room/${GET_CURRENT_ROOM}`, {
    mnemo: roomMnemo,
    params: {
      hasDocumentToSign: true,
    },
  }).catch(async (error) => {
    console.error(`Error while loading room ${roomMnemo}`)

    if (error.request.status === 404) {
      await getRouter().replace({ name: NOT_FOUND_ROUTE_NAME })
    }
    // We have to check this here as well even if this is handled in the global axios interceptor
    // Because the router is not ready in axios's scope and we can't properly check things otherwise
    const roomIsUnderNdaError =
        error.request.status === 422 &&
        error?.response.data?.errorCode === 'ERR_ENVELOPE_NDA_REQUIRED'
    if (roomIsUnderNdaError) {
      getRouter().push({ name: ROOMS_ROUTE_NAME, query: { mnemoUnderNda: roomMnemo } })
    }
  })
}

export default {
  name: 'Project',
  components: {
    AppButton,
    AppLoader,
    IManageAuthDialog,
    SubscriptionDisableReasonsCard,
    WatermarkedViewerDialog,
    SignedDocumentsNotarityBanner,
  },
  data () {
    return {
      SIGNING_CHECKLIST_ADD_ROUTE_NAME,
      disabled: false,
      getRoomPromise: null,
      iManageAuthDialogIsOpened: false,
    }
  },
  head () {
    const roomName = this.$store.getters['room/roomName']
    return {
      titleTemplate: roomName ? '%s - ' + roomName + ' - Closd' : 'Closd',
    }
  },
  computed: {
    ...mapState('room', ['currentRoom', 'currentRoomFailedToLoad', 'currentRoomPending', 'currentRoomRouteDecided']),
    ...mapState('imanage', ['authCode', 'userWantIManage', 'currentTimeTimeout']),
    ...mapGetters('user', ['currentUserId', 'iManageEnabled']),
    ...mapGetters('login', ['isFullAccessLogin']),
    ...mapGetters('imanage', ['iManageLogged', 'iManageLogExpired']),
    ...mapGetters('room', ['isNotarityEnabled','roomMnemo']),
    ...mapState('files', ['watermarkedDialogData']),
    isViewWithoutYPadding () {
      const pagesWithoutPadding = [
        ENVELOPE_TEMPLATE_ADD_ROUTE_NAME,
        ENVELOPE_TEMPLATE_UPDATE_ROUTE_NAME,
        QUESTION_ANSWER_ADD_ROUTE_NAME,
        QUESTION_ANSWER_EDIT_ROUTE_NAME,
        ROOM_DOCUMENTS_ROUTE_NAME,
        ROOM_CHECKLIST_ROUTE_NAME,
        ROOM_CHECKLIST_DELETION_VALIDATION_ROUTE_NAME,
        SIGNING_CHECKLIST_ADD_ROUTE_NAME,
      ]

      return pagesWithoutPadding.includes(this.$route.name)
    },
    shouldAuthenticateOnIManage () {
      if (this.iManageLogged || !this.userWantIManage) {
        return false
      }

      let userAlreadyRefusedIManageAuth = false
      const iManageRefusalDate = getUserSetting(this.currentUserId, IMANAGE_REFUSAL_USER_SETTINGS_KEY)

      if (iManageRefusalDate) {
        const iManageRefusalDateAsTimestamp = parseInt(iManageRefusalDate)
        if (!isNaN(iManageRefusalDateAsTimestamp)) {
          userAlreadyRefusedIManageAuth = dayjs().isBefore(dayjs(iManageRefusalDateAsTimestamp).add(1, 'day'))
        }
      }

      return !userAlreadyRefusedIManageAuth
    },
    isRouteSimpleSignature () {
      return this.$route.meta.scopeAllowed === 'simple-signature'
    },
    isNotarityBannerDisplayed () {
      return this.isNotarityEnabled && this.$route.name === ROOM_SIGNED_DOCUMENTS_ROUTE_NAME
    },
  },
  async beforeRouteEnter (to, _, next) {
    if (to.meta.scopeAllowed === 'simple-signature') {
      next()
    } else {
    if (!store.getters['login/isFullAccessLogin']) {
      console.error(`Unauthorized access to the room ${to.params.mnemo}`)
      next(false)
    }

    const room = await loadCurrentRoom(to.params.mnemo)

    if (!room?.id) {
      toast.error(i18n.global.t('project.views.Project.errorLoadingRoom'))
      // We'll actually go somewhere else. This is just to avoid preventing the navigation
      next()
      return
    }
    store.dispatch(`room/${UPDATE_ACCESS_TIME}`, to.params.mnemo)

    try {
      Sentry.setTag("room.mnemo", to.params.mnemo ?? null)
      Sentry.setTag("room.id", room.id ?? null)
    } catch (e) {
      console.warn(e)
    }

    if (to.name === ROOM_DOCUMENTS_ROUTE_NAME) {
      store.dispatch(`documents/${UPDATE_ACCESS_TIME}`, to.params.mnemo)
    }
    if (to.name === ROOM_ROUTE_NAME) {
      next(getRoomDefaultRoute(room))
    } else if (to.params.roomType === ROOM_ROUTE_TYPES.UNDEFINED) {
      // Legacy route !
      next({ ...to, params: { ...to.params, roomType: getRoomRouteType(room) }})
    } else {
      next()
    }
    }
  },
  async beforeRouteUpdate(to, from) {
    if (to.name === ROOM_ROUTE_NAME) { // navigation to a room from another room
      const room = await loadCurrentRoom(to.params.mnemo)

      if (room) {
        getRouter().replace(getRoomDefaultRoute(room))
      }
    } else if (to.params.mnemo !== from.params.mnemo) {
      await loadCurrentRoom(to.params.mnemo)
    } else { // navigation within the current room
      store.dispatch(`room/${UPDATE_ACCESS_TIME}`, to.params.mnemo)

      if (to.name === ROOM_DOCUMENTS_ROUTE_NAME) {
        store.dispatch(`documents/${UPDATE_ACCESS_TIME}`, to.params.mnemo)
      }
    }
  },
  beforeRouteLeave() {
    this.SET_PROJECT_LAYOUT(false)
    store.commit(`room/${SET_CURRENT_ROOM_ROUTE_DECIDED}`, undefined)
  },
  watch: {
    iManageLogExpired (value) {
      if (value) {
        this.getImanageAuthStatus()
      }
    },
  },
  created () {
    this.applySubscriptionBrandingColor()
  },
  mounted () {
    this.SET_PROJECT_LAYOUT(true)
    if (this.isFullAccessLogin && this.iManageEnabled) {
      if (this.shouldAuthenticateOnIManage) {
        this.getImanageAuthStatus()
      }

      if (this.iManageLogged && !this.currentTimeTimeout) {
        this.START_AUTH_TIMER()
      }
    }
  },
  beforeUnmount () {
    this.killRoom()
    //  applySubscriptionBrandingColor has to be called after the room is killed to reassign the user's sub color, or the default color.
    this.applySubscriptionBrandingColor()
    this.SET_PROJECT_LAYOUT(false)
  },
  methods: {
    ...mapMutations('global', [GlobalMutationTypes.SET_PROJECT_LAYOUT]),
    ...mapActions('room', [GET_CURRENT_ROOM, 'killRoom']),
    ...mapActions('imanage', [GET_AUTH_STATUS, START_AUTH_TIMER]),
    closeWatermarkedDialog () {
      this.$store.commit(`files/${FilesMutationTypes.RESET_WATERMARKED_DIALOG_DATA}`)
    },
    applySubscriptionBrandingColor () {
      const colorSub = this.$store.getters['subscription/getSubscriptionBrandingColor']
      const theme = useTheme()
      if (this.currentRoom?.subscription?.customizationEnabled) {
        const color = this.currentRoom?.subscription?.color
        if (color) {
          theme.current.value.colors.primary = color
        }
      } else if (colorSub) {
        theme.current.value.colors.primary = colorSub
      } else {
        theme.current.value.colors.primary = vuetifyOverrideColors.primary
      }
    },
    async getImanageAuthStatus () {
      try {
        await this.GET_AUTH_STATUS(this.authCode.imConfig)
      } catch (error) {
        if (error.response?.data?.errorCode === 'ERR_REQUIRE_IMANAGE_AUTH' || !this.authCode?.imConfig) {
          this.iManageAuthDialogIsOpened = true
        } else {
          console.error(error)
        }
      }
    },
    async reloadCurrentRoom () {
      const room = await loadCurrentRoom(this.$route.params.mnemo)
      if (this.$route.name === ROOM_ROUTE_NAME && room) {
        getRouter().replace(getRoomDefaultRoute(room))
      }
      // We will always set this to true, because we're already in the room and
      // The route is either the same or has changed already and we don't mind setting this to true again
      store.commit(`room/${SET_CURRENT_ROOM_ROUTE_DECIDED}`, true)
    },
  },
}
</script>
